import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { BaseComponent } from '../../core/base.component';
import { AuthService } from '../../core/security/auth.service';
import { ProMessagingReportsService } from '../pro-messaging-reports.service';
import { appColors } from '../../core/styles/colors';
import { SummaryReportModel } from '../../api/models/summary-report-model';
import { combineLatest, Subscription } from 'rxjs';
import { DateTime } from 'luxon';
import { TopNumbersReportModel } from '../../api/models/top-numbers-report-model';
import { ReportRequest } from '../../api/models/report-request';
import { BillingCycleLimitModel, LookupModel, SmsPhoneNumberModel, SummaryKeywordsReportModel } from '../../api/models';
import { MatDialog } from '@angular/material/dialog';
import { MessageLogsDialogComponent } from '../message-logs/message-logs-dialog.component';
import { FilterOptionModel } from '../../dialogs/grid-filter/filter-option.model';
import { PhoneNumbersUtils } from '../../core/utilities/phone-numbers-helpers';
import { MatMessagingChartZoomDialogComponent } from './mat-messaging-chart-zoom-dialog.component';
import { RolesEnum } from '../../core/security/roles.enum';
import { FlexModule } from '@angular/flex-layout';
import { GridFilterComponent } from '../../dialogs/grid-filter/grid-filter.component';
import { NgIf, NgFor, DecimalPipe } from '@angular/common';
import { MessagingSummaryCardComponent } from './summary-card.component';
import { SmsChartByTimeComponent } from './chart-by-time.component';
import { ChartByKeywordsComponent } from './chart-by-keywords.component';
import { PhoneNumberPipe } from '../../core/pipes/phone-number.pipe';
import { DateTimePipe } from '../../core/pipes/date-time.pipe';

@Component({
    selector: 'app-pro-messaging-automation-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss'],
    standalone: true,
    imports: [
        FlexModule,
        GridFilterComponent,
        NgIf,
        MessagingSummaryCardComponent,
        SmsChartByTimeComponent,
        ChartByKeywordsComponent,
        NgFor,
        RouterLink,
        DecimalPipe,
        PhoneNumberPipe,
        DateTimePipe,
    ],
})
export class ProMessagingReportsSummaryComponent extends BaseComponent implements OnInit, OnDestroy {
  filterOptions: FilterOptionModel[] = [];
  additionalFilter: any = {};

  timeSeriesType: 'line' | 'bar' = 'bar';
  groupSeriesType: 'pie' | 'bar' = 'bar';
  colors = appColors;
  buttonSubmitPromise: Promise<void>;
  summarySubscription: Subscription;
  summaryKeywordsSubscription: Subscription;
  summaryByDaySubscription: Subscription;
  summaryByPhoneNumberSubscription: Subscription;
  messagingFilterSubscription: Subscription;
  grandTotals: SummaryReportModel = {
    outboundSms: 0,
    inboundSms: 0,
    failedSms: 0,
    totalSms: 0,
    outboundMms: 0,
    inboundMms: 0,
    failedMms: 0,
    totalMms: 0,
  };
  timeSeriesTotals = [];
  keywordTotals: Array<SummaryKeywordsReportModel> = [];
  topNumbersByAll: TopNumbersReportModel[] = [];
  topOutgoingNumbersByAll: TopNumbersReportModel[] = [];
  topIncomingNumbersByAll: TopNumbersReportModel[] = [];
  messagingPhoneNumbers: SmsPhoneNumberModel[] = [];
  messagingUserEmails: LookupModel[] = [{ id: 'system', text: 'System' }];

  messagingLimits: BillingCycleLimitModel = {
    limits: 0,
    currentUsage: 0,
    billingCycleStart: '',
    currentBillingCycleStart: '',
    currentBillingCycleEnd: '',
  };

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    public dialog: MatDialog,
    public reportsService: ProMessagingReportsService,
  ) {
    super(route, headerService, titleService, authService);
  }

  async ngOnInit() {
    await super.ngOnInit();

    this.summarySubscription = this.reportsService.summaryList.subscribe(data => {
      this.grandTotals = data || {
        outboundSms: 0,
        inboundSms: 0,
        failedSms: 0,
        totalSms: 0,
        outboundMms: 0,
        inboundMms: 0,
        failedMms: 0,
        totalMms: 0,
      };

      this.viewRoles = [
        RolesEnum.ProMessagingReports_View,
        RolesEnum.ProMessagingReports_Edit,
        RolesEnum.ProMessagingReports_Delete,
      ];
      this.editRoles = [RolesEnum.ProMessagingReports_Edit, RolesEnum.ProMessagingReports_Delete];
      this.deleteRoles = [RolesEnum.ProMessagingReports_Delete];
    });

    this.summaryByDaySubscription = this.reportsService.summaryListByDay.subscribe(data => {
      let maxDate = DateTime.fromISO('1790-01-01');
      this.timeSeriesTotals = data.map((item: any) => {
        const localDateTime = DateTime.fromISO(item.day); // .setZone(Settings.defaultZone);

        if (localDateTime > maxDate) {
          maxDate = localDateTime;
        }

        return {
          day: new Date(item.day), // DateUtils.localDateFromDateTimeOnlyDate(localDateTime),
          inboundSms: item.inboundSms,
          outboundSms: item.outboundSms,
          failedSms: item.failedSms,
        };
      });
    });

    this.summaryKeywordsSubscription = this.reportsService.summaryListByKeywords.subscribe(data => {
      this.keywordTotals = data || [];
    });

    this.summaryByPhoneNumberSubscription = this.reportsService.summaryListByPhoneNumber.subscribe(data => {
      this.topNumbersByAll = data;

      for (const item of this.topNumbersByAll) {
        const messagingNumber = this.messagingPhoneNumbers.find(x => item.phoneNumber.indexOf(x.phoneNumber) > -1);
        if (messagingNumber) {
          item.userName = messagingNumber.userName;
          item.proEnabled = messagingNumber.proEnabled;
          item.smsPhoneNumber = messagingNumber.phoneNumber;
        }
      }

      this.topOutgoingNumbersByAll = this.topNumbersByAll.filter(x => x.direction === 'out');
      this.topIncomingNumbersByAll = this.topNumbersByAll.filter(x => x.direction === 'in');
    });

    this.messagingFilterSubscription = combineLatest([
      this.reportsService.messagingUserEmails,
      this.reportsService.messagingPhoneNumbers,
    ]).subscribe(([emails, phoneNumbers]) => {
      this.messagingPhoneNumbers = phoneNumbers || [];
      this.messagingUserEmails.push(...(emails || []));

      this.initFilterOptions();
    });

    this.reportsService.getMyMessagingPhoneNumbers();
    this.reportsService.getMessagingUserEmails();

    await this.updateMessagingLimits();
    // await this.getSummaryData();
  }

  async updateMessagingLimits() {
    this.messagingLimits = await this.reportsService.getMessagingLimits();
  }

  async getSummaryData() {
    // fixme this.headerService.showLoading();

    try {
      const filter: ReportRequest = {
        startDate: this.additionalFilter.startDate,
        endDate: this.additionalFilter.endDate,
        phoneNumbers: this.additionalFilter.phoneNumbers,
        userEmail: this.additionalFilter.userEmail,
      };

      await this.reportsService.getSummaryList(filter);
      await this.reportsService.getSummaryListByDay(filter);
      await this.reportsService.getSummaryKeywordsList(filter);
      await this.reportsService.getSummaryListByPhoneNumber(filter);
    } finally {
      // fixme this.headerService.hideLoading();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.hideSearchInput();
    this.summarySubscription?.unsubscribe();
    this.summaryByDaySubscription?.unsubscribe();
    this.summaryByPhoneNumberSubscription?.unsubscribe();
    this.messagingFilterSubscription?.unsubscribe();
    this.summaryKeywordsSubscription?.unsubscribe();
  }

  onSeriesChartZoomClick() {
    this.dialog.open(MatMessagingChartZoomDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        chartData: this.timeSeriesTotals,
        dialogTitle: `Message Breakdown`,
        chartType: this.timeSeriesType,
        startDate: this.additionalFilter.startDate,
        endDate: this.additionalFilter.endDate,
      },
    });
  }

  onFilterChanged(filter: any) {
    this.additionalFilter = filter;
    this.getSummaryData().then(() => {
      console.log('SummaryData fetched from server');
    });
  }

  showLogs($event: MouseEvent, type: 'incoming' | 'outgoing' | 'failed' | 'all') {
    this.dialog.open(MessageLogsDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        filter: {
          type: type,
          startDate: this.additionalFilter.startDate,
          endDate: this.additionalFilter.endDate,
          phoneNumbers: this.additionalFilter.phoneNumbers,
          userEmail: this.additionalFilter.userEmail,
        },
      },
    });
  }

  initFilterOptions(): void {
    this.filterOptions = [];

    if (this.messagingUserEmails.length > 0) {
      for (const item of this.messagingUserEmails) {
        this.filterOptions.push({
          filterKey: 'userEmail',
          multiValue: false,
          showAlways: false,
          type: 'static',
          title: 'User',
          displayValue: `${item.text}`,
          value: item.id,
          searchText: `User: ${item.text}`,
        });
      }
    }

    if (this.messagingPhoneNumbers.length > 0) {
      for (const item of this.messagingPhoneNumbers) {
        this.filterOptions.push({
          filterKey: 'phoneNumbers',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Phone Number',
          displayValue: `${PhoneNumbersUtils.format(item.phoneNumber)} ${item.userName}`,
          value: item.phoneNumberId,
          searchText: `Phone Number: ${PhoneNumbersUtils.format(item.phoneNumber)} ${item.userName} ${PhoneNumbersUtils.phoneNumberToNational(item.phoneNumber)} ${item.phoneNumber}`,
        });
      }
    }
  }
}
